import { Copy } from 'lucide-react';
import { AxiosError } from 'axios';
import * as React from 'react';
import styled from 'styled-components';
import { check_password, match_email } from 'validators-web';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownColor from '../../../components_v2/dropdown/DropdownColors';
import { DropdownStyle } from '../../../components_v2/dropdown/model/Model';
import Input from '../../../components_v2/input/Input';
import { InputMode, InputStyle } from '../../../components_v2/input/model/Model';
import PageLoader from '../../../components_v2/pageLoader/PageLoader';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import Popup from '../../../components_v2/popup/Popup';
import PopupCreation from '../../../components_v2/popup/PopupCreation';
import UserScores from './UserScores';
import { generatePassword } from '../../../components_v2/utils';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { detectDialCode, getDialCodeFlag, getPhoneCodeOptions } from '../../settings/userSettings/phoneCountryCodes';

const phoneCodeOptions = getPhoneCodeOptions();
import { PrimaryColor, DarkGreySidely } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import useAlert from '../../alert/UseAlert';
import { PasswordRestriction } from '../../auth/password/Reset';
import { creationPopupInputStyle } from '../../../components_v2/popup/style/CreationFormStyle';
import { BoolCheckbox } from '../../forms/PopupField';
import { launchToastError, launchToastMessage } from '../../forms/visual-form-editor/visualFormEditor';
import { LoadingStateEnum } from '../../import/model';
import { Unhautorized } from '../../noData/NoData';
import Restricted from '../../permissions/Restricted';
import { FlexDiv } from '../../products/style';
import { Team } from '../../teams/actions';
import { getUserById, postUser, putUser, UserDetails, UserPassWordUpdate } from '../action';
import { Group } from '../model';

type UserPopupProps = {
	isOpen: boolean;
	userId?: number;
	onClose: (user?: { user: Partial<UserDetails>, id: number }) => void;
	groups: Group[] | undefined;
	teams: Team[] | undefined;
};

export default function UserPopup(props: UserPopupProps) {
	const [user, setUser] = React.useState<UserDetails>();
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>();

	React.useEffect(() => {
		if (props.userId) {
			setLoadingState(LoadingStateEnum.LOADING);
			getUserById(props.userId)
				.then(setUser)
				.finally(() => setLoadingState(LoadingStateEnum.LOADED));
		} else {
			setUser(undefined);
		}
	}, [props.userId]);

	return <Popup
		isOpen={props.isOpen}
		popupMode={PopupMode.Details}
		onClickOut={props.onClose}
	>
		<Restricted to={{ objectAction: props.userId ? 'UpdateUserManagementSettings' : 'CreateUserManagementSettings' }} fallback={<Unhautorized height='100%' />}>
			<PopupContent
				{...props}
				user={user}
				loadingState={loadingState}
			/>
		</Restricted>
	</Popup>;
}


const defaultUser: Partial<UserDetails> = { status: true, is_owner: false, groups: [], teams: [] };

function PopupContent(props: UserPopupProps & {
	user?: UserDetails;
	loadingState?: LoadingStateEnum;
}) {
	const [updatedUser, setUpdatedUser] = React.useState<Partial<UserDetails>>(props.user ?? defaultUser);
	const [password, setPassword] = React.useState<UserPassWordUpdate>(props.userId ? 'Unchanged' : 'Random');
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const [phoneIndicator, setPhoneIndicator] = React.useState<string>('33');
	const alert = useAlert();

	React.useEffect(() => {
		const phoneRaw = props.user?.phone?.startsWith('+') ? props.user.phone.slice(1) : props.user?.phone;
		setUpdatedUser(props.user ? { ...props.user, phone: phoneRaw } : defaultUser);
		setPhoneIndicator(phoneRaw ? (detectDialCode(phoneRaw) ?? '33') : '33');
	}, [props.user]);

	const localPhone = phoneIndicator && updatedUser.phone?.startsWith(phoneIndicator)
		? updatedUser.phone.slice(phoneIndicator.length)
		: updatedUser.phone ?? '';
	const phoneIsInvalid = localPhone.length > 0 && !isValidPhoneNumber(`+${phoneIndicator}${localPhone}`);

	if (props.loadingState === LoadingStateEnum.LOADING) {
		return <FlexDiv justify="center" align="center" height="100%" width="100%">
			<PageLoader />
		</FlexDiv>;
	}
	if (props.userId && !props.user && props.loadingState === LoadingStateEnum.LOADED) {
		return <Unhautorized height="100%" />;
	}

	const containerStyle: InputStyle = { ...creationPopupInputStyle, containerHeight: 'fit-content' };

	const roles = [{ label: translateToString('global.admin'), value: true }, { label: translateToString('User'), value: false }];
	const statuses = ['inactive', 'active'].map((status, i) => ({ label: translateToString(status), value: !!i })).reverse();

	const t_name = translateToString('name');
	const t_email = translateToString('email');
	const t_phone = translateToString('phone');
	const t_password = translateToString('password');

	return <PopupCreation
		title={(props.userId || updatedUser?.name?.trim()) ? updatedUser?.name?.trim() : translateToString('create_user')}
		onClose={props.onClose}
		messageButton={props.userId ? <Translate id='global.edit' /> : <Translate id='global.create' />}
		onSubmit={async() => {
			const randomPassword = password === 'Random' ? { New: generatePassword() } : undefined;
			const e164Phone = localPhone && phoneIndicator
				? parsePhoneNumber(`+${phoneIndicator}${localPhone}`).format('E.164').slice(1)
				: undefined;
			const userToSave = { ...updatedUser, phone: e164Phone };
			try {
				setLoadingState(LoadingStateEnum.LOADING);
				let userId: number;
				if (props.userId) {
					userId = props.userId;
					await putUser(props.userId, {
						user: userToSave,
						password: randomPassword ?? password,
						groups: updatedUser.groups ?? [],
						teams: updatedUser.teams ?? [],
						additional_fields: updatedUser.color ? { color: updatedUser.color } : undefined
					});
				} else {
					userId = await postUser({
						...userToSave,
						password: randomPassword ?? password,
						groups: userToSave.groups ?? [],
						teams: userToSave.teams ?? [],
						is_owner: userToSave?.is_owner ?? false,
						name: userToSave.name!,
						email: userToSave.email!,
						status: userToSave.status ?? true,
						additional_fields: userToSave.color ? { color: userToSave.color } : undefined
					});
				}
				props.onClose({ user: userToSave, id: userId });
				if (randomPassword) {
					alert({
						title: updatedUser.name ?? '',
						content: <>
							<TranslateWrapper>
								<Translate id='the_password_for_USER_has_been_updated' options={{ variables: [['USER', updatedUser.name ?? '']] }} />
							</TranslateWrapper>
							<FlexDiv gap='1ch' width='100%' padding='0 1ch 0 0'>
								<Input name='password' type='password' disableChange value={randomPassword.New} inputStyle={creationPopupInputStyle} />
								<Copy size={20} style={{ cursor: 'pointer' }} onClick={() => {
									navigator.clipboard.writeText(randomPassword.New);
									launchToastMessage(translateToString('copied_in_clipboard'));
								}} />
							</FlexDiv>
						</>,
						noButtons: true,
					});
				}
			} catch (e) {
				if ((e as AxiosError).response?.status === 409) {
					launchToastError(translateToString('email_already_exists'));
				} else {
					launchToastError(translateToString('error_please_retry'));
				}
			}
			setLoadingState(LoadingStateEnum.LOADED);

		}}
		loading={loadingState}
		canValidate={!!updatedUser.name && !!updatedUser.name.trim() && !!updatedUser.email && match_email(updatedUser.email) && (typeof password === 'string' || check_password(password.New)) && !phoneIsInvalid}
	>
		<FlexDiv gap='20px' flow='column' align='start'>
			<Input inputStyle={containerStyle} name='name-input' type='text' value={updatedUser?.name} onChange={e => setUpdatedUser(u => ({ ...u, name: e }))} label={t_name} placeholder={t_name} required />
			<Input inputStyle={containerStyle} name='email-input' type='email' value={updatedUser?.email} onChange={e => setUpdatedUser(u => ({ ...u, email: e }))} label={t_email} placeholder={t_email} required />
			<FlexDiv flow='column' width='100%' gap='4px' align='start'>
				<PhoneFieldLabel>{t_phone}</PhoneFieldLabel>
				<FlexDiv gap='8px' width='100%'>
					<Dropdown
						name='phone-indicator-userpopup'
						datalist={phoneCodeOptions}
						selectedValue={phoneCodeOptions.find(o => o.value === phoneIndicator)}
						placeholder='+--'
						searchable
						dropdownStyle={{ containerWidth: '100px', width: '100%', optionWidth: '320px', optionHeight: '350px' }}
						JSXButtonInsideContainer
						JSXButton={({ value }) => (
							<span style={{ padding: '0 8px', fontSize: '14px' }}>
								{value ? `${getDialCodeFlag(value.value as string)} +${value.value}` : '+--'}
							</span>
						)}
						onChange={option => {
							if (Array.isArray(option)) return;
							const newCode = option.value;
							const local = phoneIndicator && updatedUser.phone?.startsWith(phoneIndicator)
								? updatedUser.phone.slice(phoneIndicator.length)
								: updatedUser.phone ?? '';
							setPhoneIndicator(newCode);
							setUpdatedUser(u => ({ ...u, phone: local ? newCode + local : undefined }));
						}}
					/>
					<div style={{ flex: 1 }}>
						<Input
							inputStyle={{ ...containerStyle, containerWidth: '100%' }}
							name='phone-input'
							type='tel'
							mode={phoneIsInvalid ? InputMode.Error : undefined}
							value={localPhone}
							onChange={v => setUpdatedUser(u => ({ ...u, phone: v ? (phoneIndicator ?? '') + v : undefined }))}
							placeholder={t_phone}
						/>
					</div>
				</FlexDiv>
			</FlexDiv>
			<Dropdown
				name='dd-roles'
				datalist={roles}
				dropdownStyle={containerStyle as DropdownStyle}
				label={translateToString('role')}
				onChange={(value) => setUpdatedUser(u => ({ ...u, is_owner: value.value, roleId: value.value === true ? 1 : 2 }))}
				selectedValue={roles[updatedUser.is_owner ? 0 : 1]}
			/>
			<Dropdown
				name='dd-status'
				datalist={statuses}
				dropdownStyle={containerStyle as DropdownStyle}
				label={translateToString('status')}
				onChange={(value) => setUpdatedUser(u => ({ ...u, status: value.value }))}
				selectedValue={statuses[updatedUser.status ? 0 : 1]}
			/>
			<Dropdown
				isMulti
				name='dd-groups'
				datalist={props.groups?.map((g) => ({ value: g, label: g.name, checked: updatedUser.groups?.includes(g.id) })) ?? []}
				onChange={(groups) => setUpdatedUser({ ...updatedUser, groups: groups.map((g) => g.value.id) })}
				dropdownStyle={containerStyle as DropdownStyle}
				doNotDeleteOptionOnSelect
				checkbox
				label={translateToString('groups')}
			/>
			<Dropdown
				isMulti
				name='dd-teams'
				datalist={props.teams?.map((t) => ({ value: t, label: t.name, checked: updatedUser.teams?.includes(t.id) })) ?? []}
				onChange={(teams) => setUpdatedUser({ ...updatedUser, teams: teams.map((t) => t.value.id) })}
				dropdownStyle={containerStyle as DropdownStyle}
				doNotDeleteOptionOnSelect
				checkbox
				label={translateToString('teams')}
			/>
			<DropdownColor
				label={translateToString('color')}
				dropdownStyle={{ labelUp: true, containerHeight: 'fit-content', optionWidth: '100%', containerWidth: '100%', jsxButtonContainerJustify: 'start' }}
				name='colorPicker'
				autoOptionUp
				selectedValue={updatedUser.color}
				onChange={v => {
					setUpdatedUser({ ...updatedUser, color: v.value });
				}}
			/>
			{props.userId && <FlexDiv fontSize='13px' gap='5px' cursor='pointer' onClick={() => setPassword(password === 'Unchanged' ? 'Random' : 'Unchanged')}><BoolCheckbox type='checkbox' checked={password !== 'Unchanged'} /><Translate id='update_password' /></FlexDiv>}
			{password !== 'Unchanged' && <FlexDiv flow='column' gap='1ch' align='start'>
				<FlexDiv gap='5px'>
					<RadioButton type='radio' name="password" id='random' value='random' checked={password === 'Random'} onChange={() => setPassword('Random')} />
					<Label htmlFor="random">
						<Translate id='generate_password' />
					</Label>
				</FlexDiv>
				<FlexDiv gap='5px'>
					<RadioButton type='radio' name="password" id='set' value='set' onChange={() => setPassword({ New: '' })} />
					<Label htmlFor="set">
						<Translate id='create_password' />
					</Label>
				</FlexDiv>
			</FlexDiv>}
			{typeof password !== 'string' && <>
				<Input inputStyle={containerStyle} name='password-input' type='password' value={password.New} onChange={(p) => setPassword({ New: p })} label={t_password} placeholder={t_password} required />
				<RestrictionContainer>
					<PasswordRestriction password={password.New} color={DarkGreySidely} fontSize={'12px'} />
				</RestrictionContainer>
			</>}
			{props.userId && <UserScores userId={props.userId} />}
		</FlexDiv>
	</PopupCreation>;
}

const RestrictionContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	width: 100%;
	text-align: center;
`;


const RadioButton = styled.input`
	accent-color: ${PrimaryColor};
`;

const Label = styled.label`
	font-size: 12px;
	margin: 0;
`;

const TranslateWrapper = styled.div`
	text-wrap: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
`;

const PhoneFieldLabel = styled.span`
	font-size: 12px;
	color: ${DarkGreySidely};
`;
